import type React from "react";
import { twJoin } from "tailwind-merge";

import type { TextAreaAppearance } from "./TextArea";

const HIGHLIGHT_APPEARANCE_CLASSES = {
  field: "rounded-lg border border-transparent p-space-sm",
  bare: "py-space-sm pl-space-xs",
  composer: "p-0",
} satisfies Record<TextAreaAppearance, string>;

interface TextAreaHighlightProps {
  content: string;
  ranges: readonly { start: number; end: number }[];
  appearance: TextAreaAppearance;
  startAdornment?: boolean;
  wrap?: React.TextareaHTMLAttributes<HTMLTextAreaElement>["wrap"];
  reserveScrollbarGutter?: boolean;
  ref?: React.Ref<HTMLDivElement>;
}

/** Mirrors content using in-bounds UTF-16 ranges ordered by start without overlaps. */
export function TextAreaHighlight({
  content,
  ranges,
  appearance,
  startAdornment,
  wrap,
  reserveScrollbarGutter,
  ref,
}: TextAreaHighlightProps): React.ReactNode {
  const parts: React.ReactNode[] = [];
  let cursor = 0;
  for (const range of ranges) {
    parts.push(content.slice(cursor, range.start));
    parts.push(
      <span key={range.start} className="bg-background-brand-light text-text-brand-secondary">
        {content.slice(range.start, range.end)}
      </span>,
    );
    cursor = range.end;
  }
  parts.push(content.slice(cursor));

  return (
    <div
      ref={ref}
      aria-hidden
      className={twJoin(
        "pointer-events-none absolute inset-0 z-0 overflow-hidden text-body text-text-primary",
        HIGHLIGHT_APPEARANCE_CLASSES[appearance],
        startAdornment && "pl-field-adornment",
        reserveScrollbarGutter && "scrollbar-gutter-stable",
      )}
    >
      <div className={wrap === "off" ? "whitespace-pre" : "wrap-break-word whitespace-pre-wrap"}>{parts}</div>
    </div>
  );
}
